import React, { useState } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Switch,
  VStack,
  Box,
  Text,
  FormControl,
  FormLabel,
  Divider,
  Icon,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSlash } from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import { buildDncUpdate, computeDnc, dncSummary } from '../../utils/dnc';

/**
 * DncControl — popover with the 4-category Do Not Contact toggles
 * (Everything / Call / Email / SMS). Writes directly to crm_contacts and keeps
 * the master/channel invariants via buildDncUpdate. Intended for the deal page
 * header (immediate write). The contact prop should expose camelCase flags:
 * dnc, dncCall, dncEmail, dncSms.
 */
export default function DncControl({ contactId, contact, onChanged, isDisabled = false }) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const activeBg = useColorModeValue('red.50', 'red.900');
  const activeBorder = useColorModeValue('red.200', 'red.700');

  const everything = !!(contact && contact.dnc);
  const call = !!(contact && contact.dncCall);
  const email = !!(contact && contact.dncEmail);
  const sms = !!(contact && contact.dncSms);
  const anyActive = everything || call || email || sms;
  const summary = dncSummary(contact);

  const apply = async (change) => {
    if (!contactId || !isSupabaseConfigured()) return;
    const current = { dncCall: call, dncEmail: email, dncSms: sms };
    const update = buildDncUpdate(current, change);
    setSaving(true);
    const { error } = await supabase.from('crm_contacts').update(update).eq('id', contactId);
    setSaving(false);
    if (error) {
      toast({ title: 'Failed to update DNC', description: error.message, status: 'error', duration: 3000, isClosable: true });
      return;
    }
    toast({ title: 'DNC updated', status: 'success', duration: 1200 });
    if (onChanged) onChanged(computeDnc(current, change));
  };

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button
          size="xs"
          variant="outline"
          leftIcon={<Icon as={FiSlash} />}
          colorScheme={anyActive ? 'red' : 'gray'}
          bg={anyActive ? activeBg : 'transparent'}
          borderColor={anyActive ? activeBorder : undefined}
          isDisabled={isDisabled}
          flexShrink={0}
          maxW={{ base: '160px', md: 'none' }}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {anyActive ? `DNC: ${summary}` : 'DNC'}
        </Button>
      </PopoverTrigger>
      <PopoverContent width="270px">
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="semibold" fontSize="sm">Do Not Contact</PopoverHeader>
        <PopoverBody>
          <VStack align="stretch" spacing={3}>
            <FormControl display="flex" alignItems="center" justifyContent="space-between" gap={3}>
              <Box>
                <FormLabel mb={0} fontSize="sm">Everything</FormLabel>
                <Text fontSize="xs" color="gray.500">Block all calls, texts &amp; emails</Text>
              </Box>
              <Switch size="sm" colorScheme="red" isChecked={everything} isDisabled={isDisabled || saving}
                onChange={(e) => apply({ everything: e.target.checked })} />
            </FormControl>
            <Divider />
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0} fontSize="sm">Calls / Phone</FormLabel>
              <Switch size="sm" colorScheme="red" isChecked={call} isDisabled={isDisabled || saving}
                onChange={(e) => apply({ call: e.target.checked })} />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0} fontSize="sm">Emails</FormLabel>
              <Switch size="sm" colorScheme="red" isChecked={email} isDisabled={isDisabled || saving}
                onChange={(e) => apply({ email: e.target.checked })} />
            </FormControl>
            <FormControl display="flex" alignItems="center" justifyContent="space-between">
              <FormLabel mb={0} fontSize="sm">Texts / SMS</FormLabel>
              <Switch size="sm" colorScheme="red" isChecked={sms} isDisabled={isDisabled || saving}
                onChange={(e) => apply({ sms: e.target.checked })} />
            </FormControl>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
